import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import { useToastStore } from "stores/toastStore/toastStore";
import { toastActions } from "stores/toastStore/toastStore.actions";
import { useCompanyFilterOptions } from "web-apps/company/components/common/hooks/useCompanyFilterOptions";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { toTeamOptions } from "./addStaffPanel.options.logic";
import type {
  StampClockIdentifier,
  StampClockRegistrationFormData,
} from "./stampClockRegistration.contract";
import { registerStampClockWorkerRequest } from "./stampClockRegistration.request.logic";
import {
  getStampClockRegistrationFormFieldErrors,
  isStampClockRegistrationFormComplete,
  type StampClockRegistrationFieldErrors,
} from "./stampClockRegistration.validator";

interface UseStampClockRegistrationLogicParams extends StampClockIdentifier {
  readonly onRegistered: () => void;
}

export interface StampClockRegistrationLogicResult {
  readonly fieldErrors: StampClockRegistrationFieldErrors;
  readonly formData: StampClockRegistrationFormData;
  readonly isFormStarted: boolean;
  readonly isSubmitDisabled: boolean;
  readonly isSubmitting: boolean;
  readonly submitError: string;
  readonly teamId: string;
  readonly teamOptions: readonly SearchableDropdownOption[];
  readonly onSubmitClick: () => void;
  readonly setField: <K extends keyof StampClockRegistrationFormData>(
    field: K,
    value: StampClockRegistrationFormData[K],
  ) => void;
}

export const useStampClockRegistrationLogic = (
  params: UseStampClockRegistrationLogicParams,
): StampClockRegistrationLogicResult => {
  const onRegistered = params.onRegistered;
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;
  const [, , toastDispatch] = useToastStore();
  const { unitTeamOptions } = useCompanyFilterOptions({
    companyId,
    loadPersonOptions: false,
  });
  const teamOptions = useMemo(
    () => toTeamOptions(unitTeamOptions),
    [unitTeamOptions],
  );

  const [initialFormData] = useState<StampClockRegistrationFormData>(() => ({
    personalIdentityNumber: params.personalIdentityNumber ?? "",
    phoneNumber: params.phoneNumber ?? "",
    firstName: "",
    lastName: "",
    teamId: null,
  }));
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // The teams may still be loading when the panel opens, so the first team is
  // picked once they are there, until another team is chosen.
  const teamId = formData.teamId ?? teamOptions[0]?.value ?? "";

  const setField = useCallback(
    <K extends keyof StampClockRegistrationFormData>(
      field: K,
      value: StampClockRegistrationFormData[K],
    ): void => {
      setFormData((previous) => ({ ...previous, [field]: value }));
      setSubmitError("");
    },
    [],
  );

  const fieldErrors = useMemo(
    () => getStampClockRegistrationFormFieldErrors(formData),
    [formData],
  );
  const isComplete = isStampClockRegistrationFormComplete({ formData, teamId });
  const isFormStarted =
    JSON.stringify(formData) !== JSON.stringify(initialFormData);

  const onSubmitClick = useCallback((): void => {
    if (!isComplete || isSubmitting || companyId == null) return;

    setIsSubmitting(true);
    setSubmitError("");
    void registerStampClockWorkerRequest({
      companyId,
      formData,
      teamId,
      translate: t,
    }).then((result) => {
      if (!isMountedRef.current) return;
      setIsSubmitting(false);
      if (!result.succeeded) {
        setSubmitError(result.message);
        return;
      }
      toastActions(toastDispatch).showToast({
        variant: "success",
        message: t("Staff.AddStaffPanelStampClockRegistered"),
      });
      onRegistered();
    });
  }, [
    companyId,
    formData,
    isComplete,
    isSubmitting,
    onRegistered,
    t,
    teamId,
    toastDispatch,
  ]);

  return {
    fieldErrors,
    formData,
    isFormStarted,
    isSubmitDisabled: !isComplete || isSubmitting,
    isSubmitting,
    submitError,
    teamId,
    teamOptions,
    onSubmitClick,
    setField,
  };
};
